import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import toast from "react-hot-toast";
import { OkxBitcoinAccount } from "types/baseType";
import { getStore, setStore } from "utils/store";

interface OkxBitcoinWalletProps {
  address: string
  publicKey: string
  isConnected: boolean
  getProvider: () => any
  getAccounts: () => Promise<string>
  disconnected: () => void
  connect: () => void
}

const OkxBitcoinWalletContext = createContext<OkxBitcoinWalletProps | null>(null);

export const OkxBitcoinWalletProvider = ({ children }: { children: ReactNode }) => {
    const [address, setAddress] = useState('')
    const [publicKey, setPublicKey] = useState('')
    const [isConnected, setIsConnected] = useState(false)

    const getProvider = useCallback(() => {
        if(typeof (window as any).okxwallet !== 'undefined' && typeof (window as any).okxwallet.bitcoin !== 'undefined') {
            return (window as any).okxwallet.bitcoin
        } else {
            return undefined
        }
    }, [])

    const getAccounts = useCallback(async() => {
        const providers = getProvider()
        if(!providers) return
        const result = await providers.connect()
        if(result) {
            return result
        } else {
            return ''
        }
    }, [getProvider])

    // const getBalance = useCallback(async () => {
    //     const providers = getProvider()
    //     if(!providers) return
    //     const result = await providers.getBalance()
    //     return result
    // }, [getProvider])

    const walletConnected = useCallback((accountInfo: OkxBitcoinAccount) => {
        setAddress(accountInfo.address)
        setPublicKey(accountInfo.compressedPublicKey)
        setIsConnected(true)
        setStore('chainge.curWallet', 'okx_bitcoin')
    }, [])

    const disconnected = useCallback(() => {
        setAddress('')
        setPublicKey('')
        setIsConnected(false)
        setStore('chainge.curWallet', '')
    }, [])

    useEffect(() => {
        const providers = getProvider()
        if(!providers) return
        const fnAccountsChanged = (addressInfo: OkxBitcoinAccount) => {
            if(addressInfo) {
                walletConnected(addressInfo)
            } else {
                disconnected()
            }
        }
        
        try {
          providers.on('accountsChanged', fnAccountsChanged);
        } catch(error) {
          console.log(1231231, error)
        };

        return () => {
            providers.removeListener('accountsChanged', fnAccountsChanged)
        }
    }, [disconnected, getProvider, walletConnected])


    const connect = useCallback(async () => {
        const provider = getProvider()
        if(!provider) {
            disconnected()
            toast( `OkxWallet not installed`,
                {
                  duration: 3000,
                  position: 'top-right',
                  icon: '⚠️'
                }
            );
            return
        }
        const accountInfo = await provider.connect()
        if(accountInfo) {
            walletConnected(accountInfo)
        } else {
            disconnected()
        }

    }, [disconnected, getProvider, walletConnected])

    useEffect(() => {
        const fnTemp = async () => {
           const result = await getAccounts();
           if(result) {
            walletConnected(result)
           } else {
            disconnected()
           }
        }
        const walletName = getStore('chainge.curWallet')
        if(walletName === 'okx_bitcoin') {
            fnTemp()
        }
    }, [disconnected, getAccounts, walletConnected])

  return (
    <OkxBitcoinWalletContext.Provider
      value={{
        address,
        publicKey,
        isConnected,
        connect,
        disconnected,
        getAccounts,
        getProvider
      }}
    >
      {children}
    </OkxBitcoinWalletContext.Provider>
  );
};

export const useOkxBitcoinWallet = () => {
  return (
    useContext(OkxBitcoinWalletContext) || {
      address: '',
      publicKey: '',
      isConnected: false,
      connect: () => {},
      disconnected: () => {},
      getAccounts: () => {},
      getProvider: () => {}
    } as OkxBitcoinWalletProps
  );
};
